import { apiRequest } from '../../api/client';

const unwrap = <T>(promise: Promise<{ data: T }>): Promise<T> => promise.then((e) => e.data);

export type MemberStatus = 'Active' | 'Suspended' | 'Anonymized';
export type Gender = 'Male' | 'Female' | 'Other';
export type OtpChannel = 'Sms' | 'Zns';
export type VoucherState = 'Unused' | 'Used' | 'Expired' | 'Refunded';
export type PointEntryType =
  | 'Earn'
  | 'Redeem'
  | 'Reversal'
  | 'Adjust'
  | 'Expire'
  | 'ReferralBonus'
  | 'WelcomeBonus'
  | 'SessionShare'
  | 'ReviewBonus';

export interface MemberSearchRow {
  id: string;
  memberCode: string;
  name: string | null;
  phone: string;
  status: MemberStatus;
  tierCode: string | null;
  spendablePoints: number;
  createdAt: string;
}

export interface MemberSearchPage {
  items: MemberSearchRow[];
  total: number;
}

export interface LedgerEntryRow {
  id: string;
  entryType: PointEntryType;
  spendableDelta: number;
  tierDelta: number;
  sourceType: string;
  note: string | null;
  createdAt: string;
}

export interface VoucherRow {
  id: string;
  code: string;
  title: string;
  state: VoucherState;
  pointsPaid: number;
  expiresAt: string;
}

export interface MemberProfile360 {
  id: string;
  memberCode: string;
  name: string | null;
  phone: string;
  dateOfBirth: string | null;
  gender: Gender | null;
  status: MemberStatus;
  createdAt: string;
  consentedAt: string | null;
  tierCode: string | null;
  tierPoints: number;
  tierPointsInCycle: number;
  tierSince: string | null;
  spendablePoints: number;
  ledgerSum: number;
  balanceMatchesLedger: boolean;
  billScanCount: number;
  billScanTotalAmount: number;
  lastActivityAt: string | null;
  recentLedger: LedgerEntryRow[];
  vouchers: VoucherRow[];
}

export interface PhoneChangeOtpResponse {
  expiresInSeconds: number;
  sentVia: OtpChannel;
  fellBack: boolean;
}

export interface PhoneChangeResult {
  memberId: string;
  oldPhone: string;
  newPhone: string;
}

export const PAGE_SIZE = 25;

export const memberKeys = {
  all: ['admin-members'] as const,
  search: (query: string, skip: number, take: number) =>
    [...memberKeys.all, 'search', query, skip, take] as const,
  profile: (id: string) => [...memberKeys.all, '360', id] as const,
};

export const membersApi = {
  search: (query: string, skip = 0, take = PAGE_SIZE) => {
    const params = new URLSearchParams();
    const q = query.trim();
    if (q) params.set('query', q);
    params.set('skip', String(skip));
    params.set('take', String(take));
    return unwrap(apiRequest<MemberSearchPage>(`/admin/members?${params.toString()}`));
  },

  profile360: (id: string) =>
    unwrap(apiRequest<MemberProfile360>(`/admin/members/${id}/360`)),

  requestPhoneChangeOtp: (id: string, newPhone: string) =>
    unwrap(
      apiRequest<PhoneChangeOtpResponse>(`/admin/members/${id}/change-phone/request-otp`, {
        method: 'POST',
        body: { newPhone },
      }),
    ),

  confirmPhoneChange: (id: string, newPhone: string, code: string) =>
    unwrap(
      apiRequest<PhoneChangeResult>(`/admin/members/${id}/change-phone/confirm`, {
        method: 'POST',
        body: { newPhone, code },
      }),
    ),

  /** Xóa tài khoản theo yêu cầu ngoài app (`S4-BE-22`): ẩn danh, không hoàn tác được. */
  erase: (id: string, reason: string) =>
    unwrap(apiRequest<unknown>(`/admin/members/${id}/erase`, { method: 'POST', body: { reason } })),
};

export const STATUS_LABELS: Record<MemberStatus, string> = {
  Active: 'Đang hoạt động',
  Suspended: 'Tạm khóa',
  Anonymized: 'Đã ẩn danh',
};

export const STATUS_TONE: Record<MemberStatus, string> = {
  Active: 'success',
  Suspended: 'warning',
  Anonymized: 'default',
};

export const GENDER_LABELS: Record<Gender, string> = {
  Male: 'Nam',
  Female: 'Nữ',
  Other: 'Khác',
};

export const VOUCHER_STATE_LABELS: Record<VoucherState, string> = {
  Unused: 'Chưa dùng',
  Used: 'Đã dùng',
  Expired: 'Hết hạn',
  Refunded: 'Đã hoàn',
};

export const VOUCHER_STATE_TONE: Record<VoucherState, string> = {
  Unused: 'success',
  Used: 'default',
  Expired: 'default',
  Refunded: 'warning',
};

export const ENTRY_TYPE_LABELS: Record<PointEntryType, string> = {
  Earn: 'Tích điểm',
  Redeem: 'Đổi quà',
  Reversal: 'Đảo điểm',
  Adjust: 'Điều chỉnh',
  Expire: 'Hết hạn',
  ReferralBonus: 'Thưởng mời bạn',
  WelcomeBonus: 'Thưởng chào mừng',
  SessionShare: 'Chia điểm bàn',
  ReviewBonus: 'Thưởng đánh giá',
};

export const OTP_CHANNEL_LABELS: Record<OtpChannel, string> = {
  Sms: 'tin nhắn SMS',
  Zns: 'Zalo',
};

/** DB lưu E.164 (`+8490…`); hiện dạng nội địa `090…`. */
export function formatPhoneNational(e164: string): string {
  if (e164.startsWith('+84') && e164.length > 3) return `0${e164.slice(3)}`;
  return e164;
}

export function formatVnDate(iso: string): string {
  return new Date(iso).toLocaleDateString('vi-VN', { timeZone: 'Asia/Ho_Chi_Minh' });
}

export function formatVnDateTime(iso: string): string {
  const parts = new Intl.DateTimeFormat('vi-VN', {
    timeZone: 'Asia/Ho_Chi_Minh',
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).formatToParts(new Date(iso));

  const get = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((p) => p.type === type)?.value ?? '';

  return `${get('day')}/${get('month')} ${get('hour')}:${get('minute')}`;
}

export function formatDob(isoDate: string): string {
  const [year, month, day] = isoDate.split('-');
  if (!year || !month || !day) return isoDate;
  return `${day}/${month}/${year}`;
}

export function displayName(name: string | null): string {
  return name?.trim() ? name : 'Chưa đặt tên';
}
